import { __read, __rest, __spread, __values } from "tslib";
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/**
 * `@virtuoso.dev/react-urx` exports the [[systemToComponent]] function.
 * It wraps urx systems in to UI **logic provider components**,
 * mapping the system input and output streams to the component input / output points.
 *
 * ### Simple System wrapped as React Component
 *
 * ```tsx
 * const sys = system(() => {
 *   const foo = statefulStream(42)
 *   return { foo }
 * })
 *
 * const { Component: MyComponent, useEmitterValue } = systemToComponent(sys, {
 *   required: { fooProp: 'foo' },
 * })
 *
 * const Child = () => {
 *   const foo = useEmitterValue('foo')
 *   return <div>{foo}</div>
 * }
 *
 * const App = () => {
 *   return <Comp fooProp={42}><Child /><Comp>
 * }
 * ```
 *
 * @packageDocumentation
 */
import React from 'react';
import * as u from '../urx';
/** @internal */
function omit(keys, obj) {
    var result = {};
    var index = {};
    var idx = 0;
    var len = keys.length;
    while (idx < len) {
        index[keys[idx]] = 1;
        idx += 1;
    }
    for (var prop in obj) {
        if (!index.hasOwnProperty(prop)) {
            result[prop] = obj[prop];
        }
    }
    return result;
}
var useIsomorphicLayoutEffect = typeof document !== 'undefined' ? React.useLayoutEffect : React.useEffect;
/**
 * Converts a system spec to React component by mapping the system streams to component properties, events and methods. Returns hooks for querying and modifying
 * the system streams from the component's child components.
 * @param systemSpec The return value from a [[system]] call.
 * @param map The streams to props / events / methods mapping Check [[SystemPropsMap]] for more details.
 * @param Root The optional React component to render. By default, the resulting component renders nothing, acting as a logical wrapper for its children.
 * @returns an object containing the following:
 *  - `Component`: the React component.
 *  - `useEmitterValue`: a hook that lets child components use values emitted from the specified output stream.
 *  - `useEmitter`: a hook that calls the provided callback whenever the specified stream emits a value.
 *  - `usePublisher`: a hook which lets child components publish values to the specified stream.
 *  <hr />
 */
export function systemToComponent(systemSpec, map, Root) {
    var requiredPropNames = Object.keys(map.required || {});
    var optionalPropNames = Object.keys(map.optional || {});
    var methodNames = Object.keys(map.methods || {});
    var eventNames = Object.keys(map.events || {});
    var Context = React.createContext({});
    function applyPropsToSystem(system, props) {
        var e_1, _a, e_2, _b;
        if (system['propsReady']) {
            u.publish(system['propsReady'], false);
        }
        try {
            for (var requiredPropNames_1 = __values(requiredPropNames), requiredPropNames_1_1 = requiredPropNames_1.next(); !requiredPropNames_1_1.done; requiredPropNames_1_1 = requiredPropNames_1.next()) {
                var requiredPropName = requiredPropNames_1_1.value;
                var stream = system[map.required[requiredPropName]];
                u.publish(stream, props[requiredPropName]);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (requiredPropNames_1_1 && !requiredPropNames_1_1.done && (_a = requiredPropNames_1.return)) _a.call(requiredPropNames_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        try {
            for (var optionalPropNames_1 = __values(optionalPropNames), optionalPropNames_1_1 = optionalPropNames_1.next(); !optionalPropNames_1_1.done; optionalPropNames_1_1 = optionalPropNames_1.next()) {
                var optionalPropName = optionalPropNames_1_1.value;
                if (optionalPropName in props) {
                    var stream = system[map.optional[optionalPropName]];
                    u.publish(stream, props[optionalPropName]);
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (optionalPropNames_1_1 && !optionalPropNames_1_1.done && (_b = optionalPropNames_1.return)) _b.call(optionalPropNames_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        if (system['propsReady']) {
            u.publish(system['propsReady'], true);
        }
    }
    function buildMethods(system) {
        return methodNames.reduce(function (acc, methodName) {
            ;
            acc[methodName] = function (value) {
                var stream = system[map.methods[methodName]];
                u.publish(stream, value);
            };
            return acc;
        }, {});
    }
    function buildEventHandlers(system) {
        return eventNames.reduce(function (handlers, eventName) {
            handlers[eventName] = u.eventHandler(system[map.events[eventName]]);
            return handlers;
        }, {});
    }
    /**
     * A React component generated from an urx system
     */
    // eslint-disable-next-line react/display-name
    var Component = React.forwardRef(function (propsWithChildren, ref) {
        var _a = propsWithChildren, children = _a.children, props = __rest(_a, ["children"]);
        var _b = __read(React.useState(function () {
            return u.tap(u.init(systemSpec), function (system) { return applyPropsToSystem(system, props); });
        }), 1), system = _b[0];
        var _c = __read(React.useState(u.curry1to0(buildEventHandlers, system)), 1), handlers = _c[0];
        useIsomorphicLayoutEffect(function () {
            var e_3, _a;
            try {
                for (var eventNames_1 = __values(eventNames), eventNames_1_1 = eventNames_1.next(); !eventNames_1_1.done; eventNames_1_1 = eventNames_1.next()) {
                    var eventName = eventNames_1_1.value;
                    if (eventName in props) {
                        u.subscribe(handlers[eventName], props[eventName]);
                    }
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (eventNames_1_1 && !eventNames_1_1.done && (_a = eventNames_1.return)) _a.call(eventNames_1);
                }
                finally { if (e_3) throw e_3.error; }
            }
            return function () {
                Object.values(handlers).map(u.reset);
            };
        }, [props, handlers, system]);
        useIsomorphicLayoutEffect(function () {
            applyPropsToSystem(system, props);
        });
        React.useImperativeHandle(ref, u.always(buildMethods(system)));
        return React.createElement(Context.Provider, { value: system }, Root
            ? React.createElement(Root, omit(__spread(requiredPropNames, optionalPropNames, eventNames), props), children)
            : children);
    });
    var usePublisher = function (key) {
        return React.useCallback(u.curry2to1(u.publish, React.useContext(Context)[key]), [key]);
    };
    /**
     * Returns the value emitted from the stream.
     */
    var useEmitterValue = function (key) {
        var system = React.useContext(Context);
        var source = system[key];
        var _a = __read(React.useState(u.curry1to0(u.getValue, source)), 2), value = _a[0], setValue = _a[1];
        useIsomorphicLayoutEffect(function () {
            return u.subscribe(source, function (next) {
                if (next !== value) {
                    setValue(u.always(next));
                }
            });
        }, [source, value]);
        return value;
    };
    var useEmitter = function (key, callback) {
        var context = React.useContext(Context);
        var source = context[key];
        useIsomorphicLayoutEffect(function () { return u.subscribe(source, callback); }, [callback, source]);
    };
    return {
        Component: Component,
        usePublisher: usePublisher,
        useEmitterValue: useEmitterValue,
        useEmitter: useEmitter,
    };
}
